import React from "react";
import {
  Card,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Table,
  Badge,
  Loader,
  ActionIcon,
  Tooltip,
  Accordion,
} from "@mantine/core";
import { IconDownload, IconFileZip } from "@tabler/icons-react";
import type { MeetingDocumentCategory, MeetingDocumentEntry } from "../../../api/bidCommittee";
import {
  useMeetingDocuments,
  useDownloadMeetingDocumentsZip,
  useDownloadMeetingDocument,
} from "../hooks";

interface MeetingDocumentsTabProps {
  meetingId: string;
  meetingNumber: string;
}

const CATEGORY_ORDER: MeetingDocumentCategory[] = [
  "award-sheet",
  "single-source",
  "agenda-return-form",
  "project-summary",
  "ceo-endorsement",
  "tender-evaluation-report",
  "tender-tor",
  "agenda-tender-document",
];

function sourceLabel(source: MeetingDocumentEntry["source"]) {
  return source === "generated-pdf" ? "Generated PDF" : "Upload";
}

export const MeetingDocumentsTab: React.FC<MeetingDocumentsTabProps> = ({
  meetingId,
  meetingNumber,
}) => {
  const { data: manifest, isLoading, error } = useMeetingDocuments(meetingId);
  const downloadZip = useDownloadMeetingDocumentsZip();
  const downloadOne = useDownloadMeetingDocument();

  const safeMeetingNumber = meetingNumber.replace(/[^\w.-]+/g, "_");

  const handleDownloadAll = () => {
    downloadZip.mutate({
      meetingId,
      fileName: `BC-${safeMeetingNumber}-documents.zip`,
    });
  };

  const handleCategoryZip = (category: MeetingDocumentCategory) => {
    downloadZip.mutate({
      meetingId,
      category,
      fileName: `BC-${safeMeetingNumber}-${category}.zip`,
    });
  };

  const handleSingle = (doc: MeetingDocumentEntry) => {
    if (!doc.available) return;
    downloadOne.mutate({
      meetingId,
      documentKey: doc.key,
      fileName: doc.fileName,
    });
  };

  if (isLoading) {
    return (
      <Card>
        <Stack align="center" py="xl">
          <Loader />
          <Text c="dimmed">Loading documents...</Text>
        </Stack>
      </Card>
    );
  }

  if (error || !manifest) {
    return (
      <Card>
        <Text c="red">Failed to load meeting documents.</Text>
      </Card>
    );
  }

  const availableCount = manifest.documents.filter((d) => d.available).length;
  const isBusy = downloadZip.isPending || downloadOne.isPending;

  if (manifest.documents.length === 0) {
    return (
      <Card>
        <Text c="dimmed" ta="center" py="xl">
          No documents are linked to this meeting yet.
        </Text>
      </Card>
    );
  }

  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={3}>Documents</Title>
            <Text size="sm" c="dimmed">
              {availableCount} of {manifest.documents.length} files available for download
            </Text>
          </div>
          <Button
            leftSection={<IconFileZip size={16} />}
            onClick={handleDownloadAll}
            loading={downloadZip.isPending && !downloadZip.variables?.category}
            disabled={availableCount === 0 || isBusy}
          >
            Download all (ZIP)
          </Button>
        </Group>

        <Accordion multiple defaultValue={CATEGORY_ORDER.filter((cat) =>
          manifest.documents.some((d) => d.category === cat)
        )}>
          {CATEGORY_ORDER.map((categoryId) => {
            const catMeta = manifest.categories.find((c) => c.id === categoryId);
            const docs = manifest.documents.filter((d) => d.category === categoryId);
            if (docs.length === 0) return null;

            return (
              <Accordion.Item key={categoryId} value={categoryId}>
                <Accordion.Control>
                  <Group justify="space-between" pr="md">
                    <Text fw={500}>{catMeta?.label ?? categoryId}</Text>
                    <Badge variant="light" color={catMeta?.availableCount ? "green" : "gray"}>
                      {catMeta?.availableCount ?? 0}/{catMeta?.count ?? docs.length}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <Group>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconFileZip size={14} />}
                        onClick={() => handleCategoryZip(categoryId)}
                        loading={
                          downloadZip.isPending &&
                          downloadZip.variables?.category === categoryId
                        }
                        disabled={
                          docs.every((d) => !d.available) || isBusy
                        }
                      >
                        Download category (ZIP)
                      </Button>
                    </Group>
                    <Table striped highlightOnHover withTableBorder>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Document</Table.Th>
                          <Table.Th>Type</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th style={{ width: 60 }} />
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {docs.map((doc) => (
                          <Table.Tr key={doc.key}>
                            <Table.Td>
                              <Text size="sm">{doc.label}</Text>
                              <Text size="xs" c="dimmed">
                                {doc.fileName}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge size="sm" variant="outline">
                                {sourceLabel(doc.source)}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              {doc.available ? (
                                <Badge size="sm" color="green">
                                  Available
                                </Badge>
                              ) : (
                                <Tooltip label={doc.missingReason}>
                                  <Badge size="sm" color="gray">
                                    Unavailable
                                  </Badge>
                                </Tooltip>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Tooltip label={doc.available ? "Download" : doc.missingReason}>
                                <ActionIcon
                                  variant="light"
                                  disabled={!doc.available || isBusy}
                                  loading={
                                    downloadOne.isPending &&
                                    downloadOne.variables?.documentKey === doc.key
                                  }
                                  onClick={() => handleSingle(doc)}
                                  aria-label={`Download ${doc.label}`}
                                >
                                  <IconDownload size={16} />
                                </ActionIcon>
                              </Tooltip>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Stack>
    </Card>
  );
};
